<script setup lang="ts">
import { breakpointsTailwind, useBreakpoints } from "@vueuse/core";
import useAsync from "~/hooks/use-async";
import AuthAPI from "~/services/auth";
import { sleep } from "~/utils/sleep";
import { base64ToString, stringToBase64 } from "~/utils/base64";
import getErrorLocalePathMessage from "~/utils/locale";

const { t, te } = useI18n();
const route = useRoute();
const router = useRouter();
const formData = ref({
  username: (route.query.email as string) || "",
  password: "",
});

const onSuccess = () => router.push((route.query.redirectUrl as string) || "/");
const onDeviceLimitError = (token: string, provider: IAuthProvider) => {
  const isCredentialsProvider = provider === "credentials";

  const params = {
    token,
    provider,
    ...(isCredentialsProvider && { username: formData.value.username }),
    ...(isCredentialsProvider && { password: formData.value.password }),
  };
  router.push({
    path: "/auth/login/limit/",
    query: { data: stringToBase64(JSON.stringify(params)) },
  });
};

const onError = (error: Error) => {
  const err = error as Error & { code?: string; token: string; provider: IAuthProvider };

  if (err.code === "device_limit_exceeded")
    onDeviceLimitError(err.token, err.provider);
};
const {
  loading: loginLoading,
  error,
  run: executeLogin,
} = useAsync(AuthAPI.signin, { onSuccess, onError });
const {
  loading: socialLoginLoading,
  error: socialError,
  run: executeSocialLogin,
} = useAsync(AuthAPI.signinWithProvider, { onSuccess, onError });

const loading = computed(() => loginLoading.value || socialLoginLoading.value);
const onSubmit = () => executeLogin(formData.value.username, formData.value.password);

async function onPasswordResetSuccess() {
  await sleep(5000);

  route.query.passwordResetSuccess
    && router.push({ query: { ...route.query, passwordResetSuccess: undefined } });
}

async function onAutoLogin() {
  if (!route.query.data)
    return;

  try {
    const data = JSON.parse(base64ToString(route.query.data as string));

    if (data.provider === "credentials") {
      formData.value.username = data.username;
      formData.value.password = data.password;
      onSubmit();
    } else {
      executeSocialLogin(data.provider);
    }
  } catch (err: any) {
    error.value = err && err.message;
  }
}

const errorMessage = computed(() => error.value && getErrorLocalePathMessage(error.value, { t, te }).message
  || socialError.value && getErrorLocalePathMessage(socialError.value, { t, te }).message);
const breakpoints = useBreakpoints(breakpointsTailwind);
const lg = breakpoints.greaterOrEqual("lg");
const { canShownHeader } = inject("authLayoutData") as { canShownHeader: boolean };

useHead({ title: t("labels.sign_in") });
onMounted(() => {
  onPasswordResetSuccess();
  onAutoLogin();
});
definePageMeta({ layout: "auth" });
</script>

<template>
  <div>
    <AuthHeader mode="sign-up" :class="{ '!my-0': canShownHeader && !lg }" :transparent="!canShownHeader || lg" />

    <BaseMain>
      <div
        class="pt-32 md:pt-48 pb-12 px-4 flex items-center justify-between flex-col h-full"
      >
        <div class="w-full max-w-[400px]">
          <h1 class="text-heading-1-bold">
            {{ t("pages.auth_sign_in.welcome_message") }}
          </h1>
          <p class="mt-4 text-caption-medium text-secondary">
            {{ t("pages.auth_sign_up.enter_your_details_below") }}
          </p>

          <BaseAlert
            v-if="route.query.deviceNoLongerAllowedError"
            type="danger"
            class="mt-6"
            :text="t('pages.auth_login.device_no_longer_allowed_error')"
          />
          <BaseAlert
            v-if="route.query.sessionExpired"
            type="danger"
            class="mt-6"
            :text="t('errors.expired_session_error')"
          />
          <BaseAlert v-if="errorMessage" type="danger" class="mt-6" :text="errorMessage" />
          <BaseAlert
            v-if="route.query.passwordResetSuccess"
            type="success"
            class="mt-6"
            :text="t('pages.auth_sign_up.password_updated_successuly_text')"
          />

          <form class="mt-8" @submit.prevent="onSubmit()">
            <BaseInput
              v-model="formData.username"
              :disabled="loading"
              type="email"
              name="email"
              :label="t('labels.email')"
              autocomplete="username"
              pattern="[a-zA-Z0-9._%+\-]+@[a-z0-9.\-]+\.[a-z]{2,4}$"
              size="30"
              required
            />

            <BaseInput
              v-model="formData.password"
              :disabled="loading"
              root-class="mt-6"
              name="password"
              :label="t('labels.password')"
              autocomplete="current-password"
              hide-show-password-btn
              sensitive
              required
            />

            <div class="mt-2">
              <BaseButton
                name="forgot-password"
                tag="nuxt-link"
                class="text-sm"
                :to="{
                  path: '/auth/password/forgot/',
                  query: { email: formData.username },
                }"
                :disabled="loading"
                link
              >
                {{ t("labels.forgot_password") }}?
              </BaseButton>
            </div>

            <BaseButton
              class="mt-10 font-bold"
              name="submit-login"
              :disabled="loading || socialLoginLoading"
              full-width
            >
              {{ t("labels.sign_in") }}
            </BaseButton>
          </form>

          <AuthSocialButtons
            :disabled="socialLoginLoading"
            @click-provider="executeSocialLogin"
          />
        </div>

        <AuthTerms class="mt-12 md:mt-0 bottom-4 text-center" />
      </div>
    </BaseMain>
  </div>
</template>
